import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import IconInput from 'src/components/IconInput/IconInput';
import InputField from 'src/components/input/input';
import NavigationButtons from 'src/features/application/components/NavigationButtons';
import Text from 'src/components/Text/Text';
import { styles } from './LossScreen.styles';
import arrowDown from 'src/assets/arrowDown.png';
import { claimFieldMapping, mapSelectedClaims } from '../../helpers/LossHelpers';
import { Loss } from '@typeRoots/common/loss';
import { LossDetailsProps, Selection } from './LossTypes';
import InputHint from 'src/components/input/InputError';
import Toast from 'src/components/Toast/Toast';
import ChecklistModal from 'src/components/CheckListModal/ChecklistModal';
import YearSelectionModal from './YearSelectionModal';
import { operationsList } from './operationsList';

const LossDetails: React.FC<LossDetailsProps> = ({
  loss,
  onSaveLoss,
  onUpdateLoss,
  onBack,
  lossSaveErrorToast = false,
  isYearModalVisible,
  setYearModalVisible,
  isChecklistModalVisible,
  setChecklistModalVisible,
  handleSelectYear,
  handleSelectionChange,
}) => {
  const [currentSelection, setCurrentSelection] = useState<Selection[]>([]);
  // Generic handler for amount field changes
  const handleAmountChange = (key: keyof Loss) => (value: string) => {
    const updatedField = { [key]: { amount: parseFloat(value) || 0, currency: 'USD' } };
    onUpdateLoss({ ...loss, ...updatedField });
  };

  useEffect(() => {
    // Map claims from the selected loss to selection
    const mappedSelection = mapSelectedClaims(loss);
    setCurrentSelection(mappedSelection); // Update selection state
  }, [loss]); // Trigger when loss changes

  const selectedClaims = currentSelection.map((item) => item.subtype.name); // Extract selected claims
  // Check if all selected claims have valid non-zero amounts
  const isAmountValid = selectedClaims.every((claim) => {
    const field = claimFieldMapping[claim];
    if (!field) return false; // Invalid claim field mapping
    const fieldValue = loss[field.key];
    return (
      typeof fieldValue === 'object' &&
      fieldValue !== null &&
      'amount' in fieldValue &&
      fieldValue.amount > 0 // Ensure the amount is greater than 0
    );
  });

  const handleCloseYearModal = () => setYearModalVisible(false);

  const handleCloseChecklistModal = () => setChecklistModalVisible(false);

  const handleYearPress = () => setYearModalVisible(true);

  const handleClaimPress = () => setChecklistModalVisible(true);

  // Determine if the "Save" button should be disabled
  const isNextDisabled = !loss.lossYear || selectedClaims.length === 0 || !isAmountValid; // Include amount validation

  return (
    <View style={styles.detailsContainer}>
      <Text variant="h2">Loss details</Text>
      <View style={styles.detailsFormContainer}>
        <IconInput
          label="In which year did the loss occur?"
          testID={'loss-year-date'}
          placeholder="Please Select"
          value={loss.lossYear ? loss.lossYear.toString() : ''}
          onPress={handleYearPress}
          iconSource={arrowDown}
          iconWidth={20}
          iconHeight={20}
        />
        <IconInput
          testID={'loss-type-of-claim-lodged'}
          label="What type of claim was lodged and processed?"
          placeholder="Please Select"
          value={selectedClaims.join(', ')} // Display selected claims
          onPress={handleClaimPress}
          iconSource={arrowDown}
          iconWidth={20}
          iconHeight={20}
        />
        <InputHint hintText="Please add a separate loss if there were multiple claims of the same type in a single year" />

        {/* Dynamically render input fields for selected claims */}
        {selectedClaims.map((claim) => {
          const field = claimFieldMapping[claim];
          if (!field) return null;

          const fieldValue = loss[field.key];
          const value =
            typeof fieldValue === 'object' && fieldValue !== null && 'amount' in fieldValue
              ? fieldValue.amount?.toString()
              : '0';

          return (
            <InputField
              testID={`loss-amount-paid-for-physical-damage-${claim}`}
              key={field.key}
              label={field.label}
              placeholder="0"
              variant="amount"
              value={value}
              onChange={handleAmountChange(field.key)}
              maxLength={12}
            />
          );
        })}
        {lossSaveErrorToast && (
          <Toast
            text={'An error occurred while saving your loss. Please try again.'}
            type={'error'}
            animate={true}
          />
        )}
        <View style={styles.detailsButtomContainer}>
          <NavigationButtons
            testID={'loss-details-final-btn-'}
            onBack={onBack}
            onNext={onSaveLoss}
            nextTitle="Save"
            isNextDisabled={isNextDisabled} // Add validation logic here as needed
          />
        </View>
      </View>

      {/* Move modals here */}
      <YearSelectionModal
        testID={'loss-details-occur-year'}
        isVisible={isYearModalVisible}
        onClose={handleCloseYearModal}
        onSelectYear={handleSelectYear}
      />
      <ChecklistModal
        parentTestID={'loss-type-of-clain-lodged'}
        isVisible={isChecklistModalVisible}
        title="What type of claim was lodged and processed?"
        list={operationsList}
        initialSelections={currentSelection}
        handleSelectionChange={handleSelectionChange}
        onClose={handleCloseChecklistModal}
      />
    </View>
  );
};

export default LossDetails;
